import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useMemo, useCallback } from 'react';
import { getApiConfig } from '@/settings/api';
import { ReducersTypes } from '@/redux/reducers/Models';

export interface Flag {
    name: string;
    isActive: boolean;
}

const getFlags = async (useAutomaticApi: boolean, prefixUri: string): Promise<string[]> => {
    try {
        const { apiInstance } = getApiConfig(useAutomaticApi);
        const response = await apiInstance.get(`${prefixUri}/v1/customers/flags`);
        const flags = response.data?.data?.featureToggle || [];
        return flags;
    } catch (error) {
        console.error('Erro ao buscar flags:', error);
        throw new Error('Falha ao buscar flags do servidor');
    }
};

export const useFlags = (enabled: boolean = true) => {
    const customer = useSelector((state: ReducersTypes) => state.customerReducer);
    const user = useSelector((state: ReducersTypes) => state.userReducer);
    
    const { useAutomaticApi, prefixUri, queryKey } = useMemo(() => {
        const hasCustomerToken = Boolean(customer?.accessToken);
        const hasUserToken = Boolean(user?.accessToken);
        const useAutomaticApi = hasCustomerToken;
        const { prefixUri } = getApiConfig(useAutomaticApi);
        
        return {
            useAutomaticApi,
            prefixUri,
            queryKey: ['flags', useAutomaticApi, hasCustomerToken, hasUserToken]
        };
    }, [customer?.accessToken, user?.accessToken]);
    
    const { data: flags = [], isLoading, error, refetch } = useQuery<string[]>({
        queryKey,
        queryFn: () => getFlags(useAutomaticApi, prefixUri),
        enabled,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        retry: 2,
        refetchOnWindowFocus: false,
    });

    const formattedFlags: Flag[] = useMemo(() => 
        flags.map(name => ({
            name,
            isActive: true
        })), [flags]
    );

    const isEnabled = useCallback((flagName: string) => 
        formattedFlags.some(f => f.name === flagName), [formattedFlags]
    );
    
    const getFlag = useCallback((flagName: string) => 
        formattedFlags.find(f => f.name === flagName), [formattedFlags]
    );
    
    const hasFlag = useCallback((flagName: string) => 
        formattedFlags.some(f => f.name === flagName), [formattedFlags]
    );

    return {
        flags: formattedFlags,
        isLoading,
        error,
        
        refetch,
        
        isEnabled,
        getFlag,
        hasFlag,
    };
};


export const useSingleFlag = (flagName: string) => {
    const { flags, isLoading, error } = useFlags();
    
    const flag = useMemo(() => 
        flags.find(f => f.name === flagName), 
        [flags, flagName]
    );
    
    const isEnabled = useMemo(() => 
        Boolean(flag), 
        [flag]
    );
    
    return {
        flag,
        isEnabled,
        isLoading,
        error,
    };
};

